import React, { Fragment, useState } from "react";
import { Badge, Skeleton } from '@mui/material';
import { CalendarPicker, LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';

// Import dayjs language dictionaries
import 'dayjs/locale/ar';
import 'dayjs/locale/zh-cn'; // Simplified Chinese
import 'dayjs/locale/zh-tw'; // Traditional Chinese
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/hi';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/ru';
import 'dayjs/locale/es';
import 'dayjs/locale/sw';
import 'dayjs/locale/sv';
import 'dayjs/locale/vi';

dayjs.extend(utc);
dayjs.extend(timezone);

function Calendar({ todaysDate, loadingEvents, events, onDateSelect, selectedDate }) {
    const [dateValue, setDateValue] = useState(selectedDate || todaysDate);
    const [month, setMonth] = useState(todaysDate.month());
    const [year, setYear] = useState(todaysDate.year());
    const { i18n } = useTranslation();

    const getDayjsLocale = (lang) => {
        if (!lang) return 'en';
        if (lang === 'zh') return 'zh-cn';
        if (lang === 'zh-Hant') return 'zh-tw';
        return lang.split('-')[0]; // 'en-US' becomes 'en', 'ar' stays 'ar'
    };

    const currentLocale = getDayjsLocale(i18n.language);

    var highlightedDays = [];
    if (!loadingEvents) {
        const eventDates = events.filter((date) => date.month() === month && date.year() === year);
        highlightedDays = eventDates.map((date) => date.date());
    }

    const handleDateChange = (newValue) => {
        setDateValue(newValue);
        if (onDateSelect) {
            onDateSelect(newValue);
        }
    };

    const renderCustomDay = (day, selectedDays, pickersDayProps) => {
        const isHighlighted = !pickersDayProps.outsideCurrentMonth && highlightedDays.indexOf(day.date()) >= 0;
        return (
            <Badge
                key={day.toString()}
                overlap="circular"
                variant={isHighlighted ? "dot" : "standard"}
                sx={{
                    '& .MuiBadge-dot': {
                        backgroundColor: '#7B1FA2',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                    }
                }}
            >
                <PickersDay {...pickersDayProps} />
            </Badge>
        );
    };

    return (
        <>
            {
                loadingEvents
                    ? <Fragment>
                        <Skeleton variant={'rounded'} height={300} width={300} sx={{ margin: '2rem' }} animation={'wave'} />
                    </Fragment>

                    : <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale}>
                        <CalendarPicker
                            date={dateValue}
                            onChange={handleDateChange}
                            onMonthChange={(newValue) => setMonth(newValue.month())}
                            onYearChange={(newValue) => setYear(newValue.year())}
                            reduceAnimations={true}
                            views={['year', 'month', 'day']}
                            renderDay={renderCustomDay}
                        />
                    </LocalizationProvider>
            }
        </>
    )
}

export default Calendar;
